import React, { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { loadYouTubeApi } from "../lib/youtubeApi";
import { useIsTouchDevice } from "../lib/useIsTouchDevice";

/**
 * Iframe YouTube pilotée par l'API officielle.
 * - `mode="preview"` : aperçu au survol sur /cours. Le parent monte le composant au
 *   survol et le DÉMONTE à la sortie de la souris : plus aucun son ne peut survivre.
 * - `mode="hero"` : page détail. Démarrage automatique en muet (seule lecture auto
 *   autorisée sur mobile) puis activation du son via l'API sur desktop.
 *
 * Règle absolue : un seul lecteur YT est construit par iframe (jamais reconstruit sur
 * changement de prop), sinon le handshake postMessage casse et `onReady` n'arrive
 * jamais — c'était la cause des vidéos qui « parfois marchent, parfois pas ».
 */
export function CourseVideo({
  video,
  mode = "preview",
  title = "Aperçu vidéo",
  wantSound = false,
  posterImage = null,
  onLoaded = null,
  onPlaying = null,
}) {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const soundAppliedRef = useRef(false);
  const reloadedRef = useRef(false);
  const videoIdRef = useRef(video || {});
  const isTouch = useIsTouchDevice();
  const isTouchRef = useRef(isTouch);
  const wantSoundRef = useRef(wantSound);
  const onPlayingRef = useRef(onPlaying);
  const [muted, setMuted] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [apiReady, setApiReady] = useState(false);

  isTouchRef.current = isTouch;
  wantSoundRef.current = wantSound;
  onPlayingRef.current = onPlaying;
  videoIdRef.current = video || {};

  // Le son n'est jamais demandé automatiquement sur tactile : le navigateur le refuse
  // et la lecture reste bloquée sur la roue de chargement.
  const soundWanted = useCallback(
    () => wantSoundRef.current && !isTouchRef.current,
    []
  );

  const applySound = useCallback((want) => {
    const p = playerRef.current;
    if (!p) return;
    try {
      if (want) {
        p.unMute();
        p.setVolume(100);
      } else {
        p.mute();
      }
      setMuted(!want);
    } catch (e) {}
    if (!want) return;
    // Garde-fou : si l'activation du son a fait décrocher la lecture (politique
    // autoplay du navigateur), on revient en muet — la vidéo qui joue primera
    // toujours sur le son.
    setTimeout(() => {
      const q = playerRef.current;
      if (!q) return;
      try {
        if (q.getPlayerState?.() !== 1) {
          q.mute();
          setMuted(true);
          q.playVideo();
        }
      } catch (e) {}
    }, 700);
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then(() => {
      if (!cancelled) setApiReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Filet sur l'événement `load` de l'iframe : si YouTube est déjà en cache, le
  // navigateur peut déclencher `load` avant que React ait posé son écouteur. On
  // attendait alors indéfiniment, et l'aperçu ne démarrait jamais — c'est la cause du
  // « des fois ça marche, des fois non ».
  useEffect(() => {
    if (iframeLoaded) return undefined;
    const t = setTimeout(() => setIframeLoaded(true), 1200);
    return () => clearTimeout(t);
  }, [iframeLoaded]);

  // Construction UNIQUE du lecteur, une fois l'iframe chargée et l'API disponible.
  useEffect(() => {
    if (!iframeLoaded || !apiReady) return undefined;
    const el = iframeRef.current;
    if (!el || !window.YT?.Player) return undefined;

    let cancelled = false;
    const timers = [];

    const tryPlay = () => {
      const p = playerRef.current;
      if (cancelled || !p) return;
      try {
        if (p.getPlayerState?.() !== 1) p.playVideo();
      } catch (e) {}
    };

    const onStateChange = (e) => {
      if (cancelled) return;
      if (e.data === 1) {
        setPlaying(true);
        if (onPlayingRef.current) onPlayingRef.current();
        if (!soundAppliedRef.current) {
          soundAppliedRef.current = true;
          applySound(soundWanted());
        }
      }
    };

    // Rechargement propre de la vidéo : certaines vidéos (Reggaeton, Contemporain)
    // restaient parfois à l'état « non démarré » (-1) — le lecteur existe, mais le flux
    // n'a jamais été demandé. `loadVideoById` repart de zéro sans toucher à l'iframe.
    const reload = () => {
      const p = playerRef.current;
      if (cancelled || reloadedRef.current || !p?.loadVideoById) return;
      reloadedRef.current = true;
      soundAppliedRef.current = false;
      try {
        p.mute();
        p.loadVideoById({
          videoId: videoIdRef.current.id,
          startSeconds: videoIdRef.current.start ?? undefined,
          endSeconds: videoIdRef.current.end ?? undefined,
        });
      } catch (e) {}
    };

    const onError = () => {
      if (!cancelled) reload();
    };

    // Un seul lecteur YT par iframe, mémorisé sur l'élément : en construire un second
    // sur la même iframe (React StrictMode, re-render) casse la configuration du
    // lecteur (« staleconfig / reloadPlayerEvent ») et la vidéo reste figée.
    let player = el.__ytPlayer;
    if (player) {
      try {
        player.addEventListener("onStateChange", onStateChange);
        player.addEventListener("onError", onError);
      } catch (e) {}
      playerRef.current = player;
      timers.push(setTimeout(tryPlay, 0));
    } else {
      player = new window.YT.Player(el, {
        events: {
          onReady: () => {
            if (cancelled) return;
            tryPlay();
          },
          onStateChange,
          onError,
        },
      });
      el.__ytPlayer = player;
      playerRef.current = player;
    }

    // Filets de sécurité : si `onReady` n'arrive pas ou si le navigateur a mis la
    // lecture en pause, on relance — toujours via l'objet player, jamais avec un
    // postMessage « à l'aveugle ». Après 5 s toujours à l'arrêt complet (-1), on
    // recharge la vidéo : c'est le cas des vidéos qui « ne se lançaient pas toujours ».
    timers.push(
      setTimeout(tryPlay, 1200),
      setTimeout(tryPlay, 3000),
      setTimeout(() => {
        const p = playerRef.current;
        if (cancelled || !p) return;
        let state = null;
        try {
          state = p.getPlayerState?.();
        } catch (e) {}
        if (state === -1 || state === 5 || state === undefined) reload();
      }, 5000),
      setTimeout(tryPlay, 6000)
    );

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      soundAppliedRef.current = false;
      reloadedRef.current = false;
      setPlaying(false);
      playerRef.current = null;
      // On NE détruit PAS le lecteur : `destroy()` supprime l'iframe du DOM alors que
      // React en est propriétaire. Si l'iframe est encore présente (re-render), on
      // coupe simplement le son ; sinon React l'a retirée et le son meurt avec elle.
      try {
        player.removeEventListener("onStateChange", onStateChange);
        player.removeEventListener("onError", onError);
      } catch (e) {}
      if (document.contains(el)) {
        try {
          player.mute();
          player.pauseVideo();
        } catch (e) {}
      }
    };
  }, [iframeLoaded, apiReady, applySound, soundWanted]);

  // Changement de la demande de son sans reconstruire le lecteur.
  useEffect(() => {
    if (!playerRef.current) return;
    soundAppliedRef.current = true;
    applySound(soundWanted());
  }, [wantSound, isTouch, applySound, soundWanted]);

  // Filet : si l'API YouTube est bloquée (extension, réseau), aucun lecteur n'est créé
  // et `onStateChange` n'arrive jamais. L'iframe joue quand même en muet (autoplay=1
  // &mute=1 dans l'URL) : on révèle l'aperçu pour ne pas laisser un carré noir. Le
  // décompte part du montage, pas du chargement de l'iframe.
  useEffect(() => {
    if (mode === "hero") return undefined;
    const t = setTimeout(() => {
      if (!playerRef.current) setPlaying(true);
    }, 3000);
    return () => clearTimeout(t);
  }, [mode]);

  if (!video || video.provider !== "youtube" || !video.id) return null;

  const posterVisible = !!posterImage && !playing;

  const params = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    loop: "1",
    playlist: video.id,
    controls: mode === "hero" ? "1" : "0",
    modestbranding: "1",
    rel: "0",
    playsinline: "1",
    disablekb: mode === "hero" ? "0" : "1",
    iv_load_policy: "3",
    enablejsapi: "1",
  });
  if (video.start != null) params.set("start", String(video.start));
  if (video.end != null) params.set("end", String(video.end));

  const src = `https://www.youtube-nocookie.com/embed/${video.id}?${params.toString()}`;

  const isVertical = video.orientation === "vertical";
  const iframeStyle = isVertical
    ? mode === "hero"
      ? {
          position: "absolute",
          top: "50%",
          left: "50%",
          height: "100%",
          width: "auto",
          aspectRatio: "9/16",
          maxWidth: "100%",
          transform: "translate(-50%, -50%) scale(1.15)",
          border: 0,
          pointerEvents: "auto",
        }
      : {
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "max(100%, calc(100% * 16/9 * 9/16))",
          height: "max(100%, calc(100% * 16/9))",
          minWidth: "100%",
          minHeight: "100%",
          transform: "translate(-50%, -50%)",
          border: 0,
          pointerEvents: "none",
        }
    : {
        border: 0,
        pointerEvents: mode === "hero" ? "auto" : "none",
      };
  const iframeClass = isVertical ? "" : "absolute inset-0 w-full h-full";

  return (
    <>
      <iframe
        ref={iframeRef}
        data-testid={`course-video-${mode}`}
        src={src}
        title={title}
        className={iframeClass}
        style={iframeStyle}
        allow="autoplay; encrypted-media; picture-in-picture"
        onLoad={() => {
          setIframeLoaded(true);
          if (onLoaded) onLoaded();
        }}
      />
      {/* Photo du cours : visible tant que la vidéo ne joue pas réellement — évite
          tout carré noir si YouTube tarde ou refuse la lecture. */}
      {posterVisible && (
        <img
          src={posterImage}
          alt={title}
          decoding="async"
          loading="eager"
          fetchPriority="high"
          data-testid="course-video-poster"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
      )}
      {mode === "hero" && !posterVisible && (
        <button
          type="button"
          onClick={() => applySound(muted)}
          data-testid="course-video-mute-toggle"
          aria-label={muted ? "Activer le son" : "Couper le son"}
          className="absolute top-24 right-6 z-30 inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm shadow-lg backdrop-blur transition-transform hover:scale-105"
          style={{ background: "rgba(255,255,255,0.92)", color: "var(--primary)" }}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          {muted ? "Activer le son" : "Couper le son"}
        </button>
      )}
    </>
  );
}
